/**
 * The per-staff page-access GRANT rule (migration 178), shared by every
 * surface that asks "may this staff member use page X?".
 *
 * The dashboard's page registry (`apps/dashboard/src/page-registry.ts`) owns
 * the pages themselves and re-exports this rule, so its backend gate, its SPA
 * nav filter and its route guard all run the code below. It lives in a leaf
 * package so a surface outside the dashboard can ask the same question with
 * the same answer: the WhatsApp picture intake offers a staff member's photo
 * for the brand-asset library only when they may use `/assets`
 * (2026-10-14, `whatsapp-picture-intake`). A second copy of the rule would be
 * a permission that disagrees with itself.
 *
 * Packages cannot import the registry (packages must not depend on apps), so
 * the always-allowed set is stated here and the registry is pinned to it by
 * `apps/dashboard/tests/page-registry-completeness.test.ts`, as the page ids a
 * package asks about are.
 */

/**
 * Ids that are always reachable whatever a restriction says. Home only: it is
 * where a blocked route redirects to and where a fail-closed viewer lands, so
 * a restriction that could remove it would be a redirect loop into a blank
 * product. Equal to the registry's `alwaysAllowed` pages.
 */
export const ALWAYS_ALLOWED_PAGE_IDS: ReadonlyArray<string> = ["home"];

/**
 * Does `granted` permit `pageId`?
 *
 * `granted === null` is UNRESTRICTED — today's behaviour and every row's
 * default. An empty ARRAY is not the same thing: it means "restricted to
 * nothing", and still yields the always-allowed pages. Collapsing the two
 * would make "untick everything" silently mean "grant everything", which is
 * the worst possible reading of that gesture.
 */
export function pageAllowed(
  granted: ReadonlyArray<string> | null,
  pageId: string,
): boolean {
  if (ALWAYS_ALLOWED_PAGE_IDS.includes(pageId)) return true;
  if (granted === null) return true;
  return granted.includes(pageId);
}
